<template>
    <van-popup
        class="account-switch-popup"
        position="bottom"
        get-container="#app"
        :value="value"
        :close-on-click-overlay="!busy"
        :overlay-style="{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }"
        @input="onInput"
    >
        <section class="account-switch-panel" role="dialog" aria-modal="true" aria-labelledby="quick-bind-title">
            <header class="account-switch-header">
                <h2 id="quick-bind-title">选择账号</h2>
                <span v-if="busy" class="quick-bind-progress" role="status" aria-live="polite">登录中 {{ progress.completed }}/{{ progress.total }}</span>
            </header>

            <p class="quick-bind-description">检测到以下账号，可一键登录</p>

            <div class="account-switch-list" role="group" aria-label="选择账号" :aria-busy="loading || busy ? 'true' : 'false'">
                <div v-if="loading" class="quick-bind-state" role="status">加载中...</div>
                <div v-else-if="error" class="quick-bind-state" role="alert">
                    <span>{{ error }}</span>
                    <button type="button" class="retry-button" :disabled="busy" @click="retry">重试</button>
                </div>
                <div v-else-if="!accounts.length" class="quick-bind-state">暂无账号</div>
                <template v-else>
                    <div
                        v-for="accountItem in accounts"
                        :key="accountItem.puuid"
                        class="account-switch-item"
                        :class="{ selected: isSelected(accountItem), busy: busy }"
                        role="checkbox"
                        :aria-checked="isSelected(accountItem) ? 'true' : 'false'"
                        :aria-disabled="busy ? 'true' : 'false'"
                        :tabindex="busy ? -1 : 0"
                        @click="toggleAccount(accountItem)"
                        @keydown.enter.prevent="toggleAccount(accountItem)"
                        @keydown.space.prevent="toggleAccount(accountItem)"
                    >
                        <svg
                            v-if="isSelected(accountItem)"
                            class="account-radio"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2Z" fill="#EB413D" />
                            <path d="M5.5 10.5L8.5 13.5L14.5 7.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <svg
                            v-else
                            class="account-radio"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path d="M10 2.5C11.9891 2.5 13.8972 3.28977 15.3037 4.69629C16.7102 6.10281 17.5 8.01088 17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5Z" stroke="#CCCCCC" />
                        </svg>
                        <span class="account-switch-name">{{ accountItem.name }}</span>
                    </div>
                </template>
            </div>

            <footer class="account-switch-footer">
                <div class="account-switch-actions">
                    <button type="button" class="cancel-button" :disabled="busy" @click="login">重新登陆</button>
                    <button type="button" class="confirm-button" :disabled="confirmDisabled" @click="confirm">一键登录</button>
                </div>
                <div class="account-switch-safe-area" aria-hidden="true"><i></i></div>
            </footer>
        </section>
    </van-popup>
</template>

<script>
export default {
    name: "ValorantQuickBindPopup",
    props: {
        value: { type: Boolean, default: false },
        accounts: { type: Array, default: () => [] },
        busy: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        error: { type: String, default: "" },
        progress: { type: Object, default: () => ({ completed: 0, total: 0 }) },
    },
    data() {
        return { selectedPuuids: [] };
    },
    computed: {
        selectedAccounts() {
            return this.accounts.filter((accountItem) => this.isSelected(accountItem));
        },
        confirmDisabled() {
            return this.busy || this.loading || !!this.error || !this.selectedAccounts.length;
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (open) this.resetSelection();
            },
        },
        accounts: {
            deep: true,
            handler() {
                this.resetSelection();
            },
        },
    },
    methods: {
        resetSelection() {
            this.selectedPuuids = [];
        },
        isSelected(accountItem) {
            return this.selectedPuuids.includes(accountItem.puuid);
        },
        toggleAccount(accountItem) {
            if (this.busy || this.loading || this.error) return;
            const index = this.selectedPuuids.indexOf(accountItem.puuid);
            if (index === -1) this.selectedPuuids.push(accountItem.puuid);
            else this.selectedPuuids.splice(index, 1);
        },
        onInput(value) {
            if (!this.busy) this.$emit("input", value);
        },
        login() {
            if (!this.busy) this.$emit("login");
        },
        retry() {
            if (!this.busy) this.$emit("retry");
        },
        confirm() {
            if (!this.confirmDisabled) this.$emit("confirm", this.selectedAccounts);
        },
    },
};
</script>

<style lang="scss" scoped>
.account-switch-popup {
    width: 100%;
    max-width: none;
    max-height: 455px;
    left: 0;
    transform: none;
    overflow: hidden;
    border-radius: 12px 12px 0 0;
    background: #222226;
    font-family: "PingFang SC", -apple-system, BlinkMacSystemFont, "Microsoft YaHei", sans-serif;
    letter-spacing: 0;
}

.account-switch-panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 455px;
    padding-top: 16px;
    box-sizing: border-box;
    color: #d7d7d9;
}

.account-switch-header {
    display: flex;
    flex: 0 0 22px;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - 32px);
    height: 22px;
    margin: 0 16px 16px;

    h2 {
        min-width: 0;
        margin: 0;
        color: #d7d7d9;
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
    }
}

.quick-bind-description {
    flex: 0 0 auto;
    margin: -8px 16px 16px;
    color: #a3a3a6;
    font-size: 12px;
    line-height: 18px;
    overflow-wrap: anywhere;
}

.quick-bind-progress {
    min-width: 0;
    margin-left: 8px;
    overflow: hidden;
    color: #a3a3a6;
    font-size: 11px;
    line-height: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.account-switch-list {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 5px;
    min-height: 48px;
    margin: 0 16px 16px;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.account-switch-item {
    display: flex;
    flex: 0 0 48px;
    align-items: center;
    width: 100%;
    height: 48px;
    padding: 12px;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 2px;
    background: #2e2e33;
    outline: none;

    &.busy { cursor: default; }
    &:focus-visible { box-shadow: inset 0 0 0 1px #eb413d; }
}

.account-radio {
    display: block;
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    margin-right: 12px;
}

.account-switch-name {
    min-width: 0;
    overflow: hidden;
    color: #d7d7d9;
    font-size: 13px;
    font-weight: 500;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.quick-bind-state {
    margin: auto 0;
    color: #a3a3a6;
    font-size: 13px;
    line-height: 20px;
    text-align: center;
    overflow-wrap: anywhere;
}

.retry-button {
    margin-left: 8px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #d7d7d9;
    font: inherit;

    &:disabled { opacity: 0.5; }
}

.account-switch-footer {
    flex: 0 0 83px;
    height: 83px;
    box-sizing: border-box;
    overflow: hidden;
    border-top: 0.5px solid #323238;
    background: #222226;
}

.account-switch-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 45px;
    padding: 5px 16px 4px;
    box-sizing: border-box;
}

.cancel-button,
.confirm-button {
    display: flex;
    flex: 1 1 0;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 8px;
    box-sizing: border-box;
    border: 0;
    border-radius: 4px;
    font: inherit;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
}

.cancel-button {
    background: transparent;
    color: #999;
}

.confirm-button {
    background: #fffffd;
    color: #212121;

    &:disabled { opacity: 0.5; }
}

.account-switch-safe-area {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    height: 38px;
    padding: 20px 0 9px;
    box-sizing: border-box;

    i {
        display: block;
        width: 134px;
        height: 5px;
        border-radius: 100px;
        background: #fffffd;
    }
}
</style>
